/**
 * Formatação brasileira e aritmética de datas de calendário.
 *
 * Regras do repo (ver CLAUDE.md):
 *  - datas são exibidas sempre como dd/MM/aaaa;
 *  - datas de calendário ("AAAA-MM-DD") são tratadas como TEXTO — nunca `new Date(iso)`,
 *    que lê a string como meia-noite UTC e, em UTC−3, recua um dia;
 *  - nada é convertido de/para UTC: a API devolve o relógio de Brasília e o front exibe o que
 *    recebeu.
 * A aritmética (somar dias, dia da semana) usa `Date.UTC` montado a partir dos componentes,
 * que não depende do fuso do navegador.
 */
import { isoToBR, pad2 } from '@components/dateInputUtils';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const ISO_DATETIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

/** "AAAA-MM-DD" ou carimbo "AAAA-MM-DDTHH:MM:SS" → "dd/MM/aaaa" ('' se vazio/inválido). */
export const formatDateBR = (value?: string | null): string => (value ? isoToBR(value) : '');

/** Carimbo "AAAA-MM-DDTHH:MM:SS" → "dd/MM/aaaa HH:mm" (sem conversão de fuso). */
export const formatDateTimeBR = (value?: string | null): string => {
  if (!value) return '';
  const m = ISO_DATETIME.exec(value);
  if (!m) return formatDateBR(value);
  return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
};

/** "AAAA-MM-DD" → "dd/MM" (cards do kanban). */
export const formatDayMonthBR = (value?: string | null): string => {
  if (!value) return '';
  const m = ISO_DATE.exec(value);
  return m ? `${m[3]}/${m[2]}` : '';
};

/** Hoje no relógio local do navegador, como "AAAA-MM-DD" (sem passar por UTC). */
export const todayISO = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;

const toUTC = (iso: string): number | null => {
  const m = ISO_DATE.exec(iso);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const fromUTC = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Soma `days` (pode ser negativo) a uma data "AAAA-MM-DD". */
export const addDaysISO = (iso: string, days: number): string => {
  const ms = toUTC(iso);
  if (ms === null) return iso;
  return fromUTC(ms + days * DAY_MS);
};

/** Dias de `from` até `to` (to − from), ambos "AAAA-MM-DD". */
export const diffDaysISO = (from: string, to: string): number => {
  const a = toUTC(from);
  const b = toUTC(to);
  if (a === null || b === null) return 0;
  return Math.round((b - a) / DAY_MS);
};

/** Dia da semana de uma data "AAAA-MM-DD" (0 = domingo … 6 = sábado). */
export const weekdayISO = (iso: string): number => {
  const ms = toUTC(iso);
  return ms === null ? 0 : new Date(ms).getUTCDay();
};

/** Primeiro e último dia do mês (month 0-based) como "AAAA-MM-DD". */
export const monthRangeISO = (year: number, month: number): { start: string; end: string } => {
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return { start: `${year}-${pad2(month + 1)}-01`, end: `${year}-${pad2(month + 1)}-${pad2(last)}` };
};

/** Iniciais para o avatar ("Ana Paula Souza" → "AS"). */
export const initials = (name?: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** "1 atividade" / "3 atividades". */
export const plural = (n: number, singular: string, pluralForm: string): string =>
  `${n} ${n === 1 ? singular : pluralForm}`;

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

/**
 * Tamanho de arquivo legível em pt-BR, base 1024: 0 → "0 B", 1536 → "1,5 KB", 5·1024² → "5 MB".
 * Até 1 casa decimal (sem zeros sobrando); bytes inteiros sem decimal.
 */
export const formatBytes = (bytes?: number | null): string => {
  if (bytes === undefined || bytes === null || !Number.isFinite(bytes) || bytes < 0) return '';
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  const digits = unit === 0 ? 0 : value >= 100 ? 0 : 1;
  const text = value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: digits });
  return `${text} ${BYTE_UNITS[unit]}`;
};

/** Duração em segundos → "mm:ss" (ou "h:mm:ss" a partir de 1 hora). Arredonda para o segundo. */
export const formatDuration = (seconds?: number | null): string => {
  if (seconds === undefined || seconds === null || !Number.isFinite(seconds) || seconds < 0) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${pad2(m)}:${pad2(s)}`;
};

/** Duração com segundos e relógio: 75.4 → "75,4 s (01:15)". */
export const formatDurationLong = (seconds?: number | null): string => {
  const clock = formatDuration(seconds);
  if (!clock) return '';
  const secs = (seconds as number).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  return `${secs} s (${clock})`;
};

/**
 * Epoch em ms (ex.: `File.lastModified`) → "AAAA-MM-DDTHH:MM:SS" nos componentes LOCAIS do
 * navegador, sem fuso — o formato de carimbo da API. Nunca `toISOString()` (que seria UTC).
 */
export const localTimestamp = (epochMs: number): string => {
  const d = new Date(epochMs);
  if (Number.isNaN(d.getTime())) return '';
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
    `T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  );
};

/** Número pt-BR com até `digits` casas ("12,3"); vazio para indefinido. */
export const formatNumberBR = (value?: number | null, digits = 1): string =>
  value === undefined || value === null || !Number.isFinite(value)
    ? ''
    : value.toLocaleString('pt-BR', { maximumFractionDigits: digits });

/** Valor em dólar no formato brasileiro: "US$ 1.234,56". Custos pequenos usam mais casas. */
export const formatUSD = (value?: number | null, digits?: number): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const d = digits ?? (Math.abs(value) > 0 && Math.abs(value) < 0.1 ? 4 : 2);
  return `US$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
};

/** Inteiro com separador de milhar pt-BR (tokens, chamadas). */
export const formatIntBR = (value?: number | null): string =>
  value === null || value === undefined ? '—' : Math.round(value).toLocaleString('pt-BR');

/** Valor digitado ("1.234,56" ou "100.5") → número; vazio ou inválido = null. */
export const parseMoney = (raw: string): number | null => {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
  return Number.isFinite(n) ? n : null;
};
